import { queryOptions } from '@tanstack/react-query'
import { AsyncJobResponse } from 'src/queries/asyncJobs/asyncJobs.models'
import { getAsyncJob } from 'src/queries/asyncJobs/asyncJobs.network'

/**
 * Poll cadence while a bulk async job is still running. The legacy flow was
 * driven by repeated Pusher `AsyncJob` events; this slice replaces that with
 * a React Query `refetchInterval` poll that stops once the job completes.
 */
export const ASYNC_JOB_POLL_INTERVAL_MS = 3000

export const isAsyncJobComplete = (
  job: AsyncJobResponse | undefined,
): boolean => job !== undefined && job.remaining === 0

export const asyncJobsQueryKeys = {
  all: () => ['asyncJobs'] as const,
  detail: (jobId: string) =>
    [...asyncJobsQueryKeys.all(), 'detail', jobId] as const,
}

export const asyncJobQueries = {
  detail: (jobId: string | undefined) =>
    queryOptions({
      queryKey: asyncJobsQueryKeys.detail(jobId ?? ''),
      queryFn: () => getAsyncJob(jobId as string),
      enabled: Boolean(jobId),
      // Always treat poll data as stale so each interval refetches.
      staleTime: 0,
      // One request per poll tick (the legacy thunk did a single GET per
      // trigger); refetchInterval is the retry loop, so a transient error
      // self-heals on the next tick rather than triggering a retry storm.
      retry: false,
      // Stop polling once the job reports no remaining work.
      refetchInterval: (query) =>
        isAsyncJobComplete(query.state.data)
          ? false
          : ASYNC_JOB_POLL_INTERVAL_MS,
    }),
}
